/**
 * SPRING Assessment — Reusable question renderers
 * Extracted from AssessmentFlow.jsx for maintainability.
 * These are the core input components used by the assessment.
 */
import React, { useState, useRef } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

/* ── Single select (radio-like) ─────────────────────────── */
export function SingleSelect({ q, value, onChange, transformLabel }) {
  const xform = transformLabel || (x => x);
  return (
    <div className="assess-options">
      {q.options.map((opt, i) => (
        <button key={i} className={`assess-option ${value === opt ? 'selected' : ''}`}
          onClick={() => onChange(opt)} data-testid={`option-${i}`}>{xform(opt)}</button>
      ))}
    </div>
  );
}

/* ── Multi select (checkbox-like) ───────────────────────── */
export function MultiSelect({ q, value, onChange, exactCount, transformLabel }) {
  const xform = transformLabel || (x => x);
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (q.exclusiveOption && opt === q.exclusiveOption) { onChange([opt]); return; }
    if (q.exclusiveOptions && q.exclusiveOptions.includes(opt)) { onChange([opt]); return; }
    const filtered = selected.filter(v => {
      if (q.exclusiveOption && v === q.exclusiveOption) return false;
      if (q.exclusiveOptions && q.exclusiveOptions.includes(v)) return false;
      return true;
    });
    if (filtered.includes(opt)) onChange(filtered.filter(v => v !== opt));
    else if (!exactCount || filtered.length < exactCount) onChange([...filtered, opt]);
  };
  return (
    <div className="assess-options">
      {q.options.map((opt, i) => (
        <button key={i} className={`assess-option ${selected.includes(opt) ? 'selected' : ''}`}
          onClick={() => toggle(opt)} data-testid={`option-${i}`}>{xform(opt)}</button>
      ))}
      {exactCount && (
        <p className={`assess-hint ${selected.length !== exactCount ? 'assess-hint-warn' : ''}`} data-testid="exact-count-hint">
          {selected.length} of {exactCount} selected
          {selected.length > 0 && selected.length < exactCount && `, choose ${exactCount - selected.length} more`}
        </p>
      )}
    </div>
  );
}

/* ── Scale input (1-N buttons) ──────────────────────────── */
export function ScaleInput({ q, value, onChange, max = 5 }) {
  const v = value || 0;
  return (
    <div className="assess-scale">
      <div className="assess-scale-labels">
        <span>{q.scaleLabels?.[1] || q.scaleLabels?.[q.scaleMin]}</span>
        <span>{q.scaleLabels?.[max] || q.scaleLabels?.[q.scaleMax]}</span>
      </div>
      <div className="assess-scale-buttons">
        {Array.from({ length: max }, (_, i) => i + 1).map(n => (
          <button key={n} className={`assess-scale-btn ${v === n ? 'active' : ''}`}
            onClick={() => onChange(n)} data-testid={`scale-${n}`}>{n}</button>
        ))}
      </div>
    </div>
  );
}

/* ── Text / voice input ─────────────────────────────────── */
export function TextInput({ q, value, onChange, placeholder }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const toggleVoice = () => {
    if (listening) { recognitionRef.current?.stop(); setListening(false); return; }
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) { toast.error('Voice input not supported'); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous = true; recognition.interimResults = true; recognition.lang = 'en-NZ';
    let finalTranscript = value || '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript + ' ';
        else interim += event.results[i][0].transcript;
      }
      onChange(finalTranscript + interim);
    };
    recognition.onend = () => setListening(false);
    recognition.start(); recognitionRef.current = recognition; setListening(true);
  };
  return (
    <div className="assess-textarea-wrap">
      <textarea className="assess-textarea" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || q?.placeholder || ''} rows={5} data-testid="text-input" />
      {('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) && (
        <button className={`assess-voice-btn ${listening ? 'active' : ''}`} onClick={toggleVoice}
          title={listening ? 'Stop recording' : 'Speak your answer'} data-testid="voice-btn">
          {listening ? <MicOff size={18} /> : <Mic size={18} />}
        </button>
      )}
    </div>
  );
}

/* ── Name fields ────────────────────────────────────────── */
export function NameFields({ q, value, onChange }) {
  const v = typeof value === 'object' && value ? value : {};
  return (
    <div className="assess-name-fields">
      {q.fields.map(f => (
        <div key={f.key} className="assess-field-group">
          <label>{f.label}{f.required && ' *'}</label>
          <input type={f.inputType || 'text'} value={v[f.key] || ''} data-testid={`name-${f.key}`}
            onChange={e => onChange({ ...v, [f.key]: e.target.value })} />
        </div>
      ))}
    </div>
  );
}
